'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { apiFetch } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import { searchPages } from '../lib/pageSearch';
import type { SearchResult } from './GlobalSearchModal';

const TYPE_LABEL_KEYS: Record<string, string> = {
  lead: 'searchTypeLead',
  opportunity: 'searchTypeOpportunity',
  case: 'searchTypeCase',
  contact: 'searchTypeContact',
  account: 'searchTypeAccount',
  owner: 'searchTypeOwner',
  'armorik-invoice': 'searchTypeArmorikInvoice',
  'armorik-order': 'searchTypeArmorikOrder',
  product: 'searchTypeProduct',
  'pohoda-order': 'searchTypePohodaOrder',
  'pohoda-invoice': 'searchTypePohodaInvoice',
  page: 'searchTypePage',
};

type HeaderSearchProps = {
  onOpenModal?: () => void;
};

export default function HeaderSearch({ onOpenModal }: HeaderSearchProps) {
  const router = useRouter();
  const { t, tLocale } = useTranslation();
  const { role, permissions } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  // Zavření dropdownu při kliku mimo
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await apiFetch<SearchResult[]>(
          `/api/user/search?q=${encodeURIComponent(query.trim())}&limitPerType=5`
        );
        if (myReqId === reqIdRef.current) {
          setResults(data || []);
          setActiveIndex(0);
        }
      } catch (e: any) {
        if (myReqId === reqIdRef.current) {
          setError(e?.message || t('compGlobalSearchError'));
          setResults([]);
        }
      } finally {
        if (myReqId === reqIdRef.current) setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const pageResults = useMemo<SearchResult[]>(
    () => searchPages(query, t, (k) => tLocale('en', k), role, permissions),
    [query, t, tLocale, role, permissions],
  );

  const flat = useMemo(() => [...pageResults, ...results], [pageResults, results]);

  const grouped = useMemo(() => {
    const groups: { type: string; items: SearchResult[] }[] = [];
    const order: string[] = [];
    const byType: Record<string, SearchResult[]> = {};
    for (const r of flat) {
      if (!byType[r.type]) {
        byType[r.type] = [];
        order.push(r.type);
      }
      byType[r.type].push(r);
    }
    for (const type of order) {
      groups.push({ type, items: byType[type] });
    }
    return groups;
  }, [flat]);

  const navigate = useCallback(
    (r: SearchResult) => {
      setOpen(false);
      setQuery('');
      setResults([]);
      router.push(r.url);
    },
    [router]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = flat[activeIndex];
      if (r) navigate(r);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  // Scroll aktivního prvku do view
  useEffect(() => {
    if (!open) return;
    const el = containerRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    if (el && 'scrollIntoView' in el) {
      (el as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, open]);

  const typeLabel = (type: string) => {
    const key = TYPE_LABEL_KEYS[type];
    return key ? t(key) : type;
  };

  const showDropdown = open && (query.trim().length >= 2 || loading || !!error);

  return (
    <div ref={containerRef} className="relative flex-shrink-0 w-40 sm:w-56 md:w-72">
      <div className="flex items-center gap-1.5 border border-wheat bg-cream rounded-rustic px-2 py-1 focus-within:border-moss focus-within:ring-1 focus-within:ring-moss transition">
        <svg className="w-4 h-4 text-wood flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('globalSearchPlaceholder')}
          className="flex-1 min-w-0 bg-transparent text-sm text-bark placeholder:text-wood/60 focus:outline-none"
          autoComplete="off"
          spellCheck={false}
        />
        {loading && <span className="text-xs text-wood animate-pulse flex-shrink-0">…</span>}
        {!loading && query.length === 0 && onOpenModal && (
          <kbd
            className="hidden md:inline-block text-xs text-wood/60 border border-wheat rounded px-1 py-0.5 flex-shrink-0 cursor-pointer hover:bg-linen"
            onClick={onOpenModal}
            title={t('globalSearchTitle')}
          >
            ⌃⇧K
          </kbd>
        )}
      </div>

      {showDropdown && (
        <div className="absolute right-0 top-full mt-1 w-80 sm:w-96 max-w-[90vw] bg-linen border border-wheat rounded-rustic shadow-xl z-[10001] overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto">
            {error && (
              <div className="px-4 py-3 text-sm text-rust">{error}</div>
            )}
            {!error && !loading && flat.length === 0 && (
              <div className="px-4 py-4 text-sm text-wood text-center">
                {t('globalSearchNoResults')}
              </div>
            )}
            {grouped.map((group) => (
              <div key={group.type} className="border-b border-wheat/40 last:border-b-0">
                <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-wood bg-cream/40">
                  {typeLabel(group.type)}
                </div>
                {group.items.map((r) => {
                  const idx = flat.indexOf(r);
                  const active = idx === activeIndex;
                  return (
                    <button
                      key={`${r.type}-${r.id}`}
                      data-idx={idx}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => navigate(r)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition ${
                        active ? 'bg-moss/15' : 'hover:bg-cream/60'
                      }`}
                    >
                      <span className="text-lg flex-shrink-0">{r.icon || '•'}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-bark truncate">{r.title}</span>
                        {r.subtitle && (
                          <span className="block text-xs text-wood truncate">{r.subtitle}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="px-3 py-1.5 border-t border-wheat bg-cream/30 text-xs text-wood/70 flex items-center justify-between">
            <span>
              <kbd className="bg-linen border border-wheat rounded px-1">↑↓</kbd>{' '}
              {t('globalSearchNavigate')}
              {' · '}
              <kbd className="bg-linen border border-wheat rounded px-1">↵</kbd>{' '}
              {t('globalSearchOpen')}
            </span>
            <span>{t('globalSearchRespectsPermissions')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
